import { StytchProvider } from "@stytch/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ConfigError } from "./components/states";
import { API_ORIGIN, LOGIN_ORIGIN } from "./config";
import { stytchClient } from "./stytch";
import "./styles.css";

const container = document.getElementById("root");
if (container === null) {
  throw new Error("missing #root element");
}

/**
 * **Three build inputs, and the app cannot do its job without any of them.**
 *
 * The public token is what lets the SDK read the session; `VITE_API_ORIGIN` is where every
 * `/app/*` request goes; `VITE_LOGIN_ORIGIN` is the only way a visitor with no session can get
 * one, because sign-in lives on that host and not this one. A bundle missing one of them
 * renders the calm config-error card rather than a page that half works — a panel that loads,
 * shows a chip, and then fails every request is a worse answer than saying so.
 *
 * Checked here, at the root, so there is one place that decides the app is unusable and every
 * component below can assume it is not.
 *
 * **There is a fourth build input, and it deliberately is not one of these** (PR #366 review).
 * `VITE_FAMILY_SELF_CREATE_ENABLED` is a posture rather than a dependency: without it the panel
 * works, and only stops offering a first-time visitor a way to start a family. That is a page
 * worth shipping, so it must not reach the config-error card — it fails closed where it is
 * read (`featureFlags.ts`) and is caught before the build instead (`cicd/deploy.mjs`), which is
 * the one place that can tell "absent" from "deliberately false".
 */
const client = API_ORIGIN !== null && LOGIN_ORIGIN !== null ? stytchClient : null;

// One <StytchProvider> for the whole app, as in the login SPA.
createRoot(container).render(
  <StrictMode>
    {client === null ? (
      <ConfigError />
    ) : (
      <StytchProvider stytch={client}>
        <App />
      </StytchProvider>
    )}
  </StrictMode>,
);
